import { useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Field, Input } from '@zt/ui';
import { api } from '../lib/api';
import { useT } from '../lib/i18n';
import type { ProductPage } from '../lib/types';

/** Birden çok ürün seçici: ad, SKU veya barkodla arar; seçim sırası korunur. */
export function ProductMultiPicker({
  value,
  onChange,
  label,
  help,
  error,
  max,
}: {
  value: string[];
  onChange: (ids: string[]) => void;
  label: string;
  help?: ReactNode;
  error?: string;
  max: number;
}) {
  const t = useT();
  const [q, setQ] = useState('');
  const [titles, setTitles] = useState<Record<string, string>>({});
  const results = useQuery({
    queryKey: ['product-picker-search', q],
    queryFn: () => api.get<ProductPage>(`/v1/admin/products?${new URLSearchParams({ q, limit: '10' })}`),
    enabled: q.trim().length >= 2,
  });
  // Seçili ürünlerin adları (ekran açılırken) bir kez okunur.
  const known = useQuery({
    queryKey: ['product-picker-known'],
    queryFn: () => api.get<ProductPage>('/v1/admin/products?limit=200'),
    enabled: value.length > 0,
  });
  const nameOf = useMemo(() => {
    const m = new Map<string, string>(Object.entries(titles));
    for (const p of known.data?.items ?? []) if (!m.has(p.id)) m.set(p.id, p.title);
    return m;
  }, [titles, known.data]);
  const labelOf = (id: string) =>
    nameOf.get(id) ??
    (known.data && !known.data.nextCursor ? t('admin.pages.unknownProduct') : `#${id.slice(0, 8)}`);
  return (
    <div className="grid gap-2">
      <Field label={label} help={help} error={error}>
        {(p) => (
          <Input
            {...p}
            type="search"
            value={q}
            placeholder={t('admin.menus.productSearch')}
            onChange={(e) => setQ(e.target.value)}
          />
        )}
      </Field>
      {results.data && results.data.items.length > 0 && (
        <ul className="max-h-48 overflow-auto rounded-sm border border-line text-sm">
          {results.data.items.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                disabled={value.includes(r.id) || value.length >= max}
                className="w-full px-3 py-1.5 text-start hover:bg-surface-2 disabled:text-ink-3"
                onClick={() => {
                  setTitles((x) => ({ ...x, [r.id]: r.title }));
                  onChange([...value, r.id]);
                  setQ('');
                }}
              >
                {r.title}
              </button>
            </li>
          ))}
        </ul>
      )}
      {value.length > 0 && (
        <ol className="grid gap-1 text-sm">
          {value.map((id) => (
            <li key={id} className="flex items-center gap-2 rounded-sm border border-line px-2 py-1">
              <span className="flex-1">{labelOf(id)}</span>
              <Button
                size="sm"
                variant="ghost"
                aria-label={`${t('common.remove')}: ${labelOf(id)}`}
                onClick={() => onChange(value.filter((x) => x !== id))}
              >
                {t('common.remove')}
              </Button>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
